import BaseStore from 'customer-flows/stores/base-store'
import { context } from 'customer-flows/utils/core'
import { type Awaitable } from 'customer-flows/utils/core'
import { tracked } from '@glimmer/tracking'
import type TicketsStore from 'customer-flows/stores/tickets'

export default class CustomQuestionStoreClass extends BaseStore {
  @context('program') program: Awaitable<ActivityProgramType>
  @context('tickets') selectedTicketNames: TicketsStore['selectedTicketNames']

  @tracked questionActivityConfigurations: QuestionActivityConfiguration[] = []
  @tracked perBookingQuestionAnswers: QuoteSparseQuestionAnswerInput[] = []
  @tracked perGuestQuestionAnswers: Map<string, QuoteSparseQuestionAnswerInput[]> = new Map()
  // tracks which questions are follow-ups and their parent question ID
  @tracked followUpQuestionParentMap: Map<string, string> = new Map()
  // tracks per-guest follow-up questions for each ticket (ticketRefId -> questionIds[])
  @tracked perGuestFollowUpQuestions: Map<string, string[]> = new Map()
  @tracked allQuestionsAreValid: boolean = false
  @tracked prePaymentQuestionCount: number = 0
  @tracked postPaymentQuestionCount: number = 0

  get getPerGuestQuestionAnswersById(): (ticketId: string, questionId: string) => QuoteSparseQuestionAnswerInput | undefined {
    return (ticketId: string, questionId: string) => {
      const answers = this.perGuestQuestionAnswers.get(ticketId) ?? []
      return answers.find(answer => answer.questionId === questionId)
    }
  }

  get perBookingQuestionConfigs(): QuestionActivityConfiguration[] {
    // filter out LOCATION questions as they are not yet implemented
    return this.questionActivityConfigurations.filter(questionConfig => {
      return !questionConfig.question.perGuest && questionConfig.question.type !== 'LOCATION'
    })
  }

  get perBookingQuestionsWithChargePerGuest(): QuestionActivityConfiguration[] {
    return this.perBookingQuestionConfigs.filter(questionConfig => {
      return questionConfig.question.options?.some(option => option.chargePerGuest)
    })
  }

  get perGuestQuestionConfigs(): QuestionActivityConfiguration[] {
    // for now, we won't return location questions until implemented
    const baseConfigs = this.questionActivityConfigurations?.filter((config: QuestionActivityConfiguration) => {
      return config.question.perGuest && config.question.type !== 'LOCATION'
    }) ?? []

    // filter by selected ticket categories - only return questions that apply to at least one selected ticket
    const selectedTickets = this.selectedTicketNames ?? []

    if (selectedTickets.length === 0) {
      return []
    }

    return baseConfigs.filter(config => {
      // if no whitelisted categories, question applies to all tickets
      if (!config.question.whitelistedCategories || config.question.whitelistedCategories.length === 0) {
        return true
      }

      // check if at least one selected ticket matches the question's whitelisted categories
      return selectedTickets.some(ticket =>
        ticket.category && config.question.whitelistedCategories.includes(ticket.category)
      )
    })
  }

  get getPerGuestQuestionConfigsForTicket(): (ticket: SelectedTicket) => QuestionActivityConfiguration[] {
    return (ticket: SelectedTicket) => {
      const ticketRefId = ticket.refId
      const baseConfigs = this.perGuestQuestionConfigs
      const followUpQuestionIds = this.perGuestFollowUpQuestions.get(ticketRefId) ?? []

      // include base configs and any follow-up questions specific to this ticket
      return baseConfigs.filter(config => {
        const categoryMatches = config.question.whitelistedCategories
          ? (ticket.category && config.question.whitelistedCategories.includes(ticket.category))
          : true
        const isFollowUp = this.followUpQuestionParentMap.has(config.question.id)

        // include follow-up questions if they're in this ticket's list, category is not important
        if (isFollowUp) {
          return followUpQuestionIds.includes(config.question.id)
        }

        // for regular questions only include if category matches
        return categoryMatches
      })
    }
  }

  get perGuestNonMandatoryQuestionConfigurations(): QuestionActivityConfiguration[] {
    return this.perGuestQuestionConfigs.filter(questionConfig => !questionConfig.isRequired)
  }

  get nonMandatoryPerBookingQuestionAnswers(): QuoteSparseQuestionAnswerInput[] {
    return this.perBookingQuestionAnswers.filter(answer => {
      return this.perBookingQuestionConfigs.find(questionConfig => questionConfig.question.id === answer.questionId)
    })
  }

  get nonMandatoryPerGuestQuestionAnswers(): Map<string, QuoteSparseQuestionAnswerInput[]> {
    const answers = new Map<string, QuoteSparseQuestionAnswerInput[]>()

    this.perGuestQuestionAnswers.forEach((ticketAnswers, ticketRefId) => {
      const filteredAnswers = ticketAnswers.filter(answer => {
        return this.perGuestNonMandatoryQuestionConfigurations.find(questionConfig => questionConfig.question.id === answer.questionId)
      })

      if (filteredAnswers.length > 0) {
        answers.set(ticketRefId, filteredAnswers)
      }
    })
    return answers
  }

  // checks if any default answers have charges to commit these charges when default answers
  // have a charge linked to them
  get hasDefaultAnswersWithCharges(): boolean {
    const hasPerBookingCharges = this.perBookingQuestionAnswers.some(answer =>
      answer.items && answer.items.length > 0 && answer.items.some(item => item.reservationStatus === 'CONFIRMED')
    )

    if (hasPerBookingCharges) {
      return true
    }

    for (const ticketAnswers of this.perGuestQuestionAnswers.values()) {
      const hasPerGuestCharges = ticketAnswers.some(answer =>
        answer.items && answer.items.length > 0 && answer.items.some(item => item.reservationStatus === 'CONFIRMED')
      )
      if (hasPerGuestCharges) {
        return true
      }
    }

    return false
  }

  /**
   * returns the answer for a per-booking question by its id
   * @param id
   */
  getQuestionAnswerById(id: string): QuoteSparseQuestionAnswerInput | undefined {
    return this.perBookingQuestionAnswers.find(answer => answer.questionId === id)
  }

  /**
   * checks if all mandatory per-guest questions are answered
   * @param tickets
   * @param forceMandatory
   */
  areAllPerGuestRequiredQuestionsAnswered(tickets: SelectedTicket[], forceMandatory: boolean = false): boolean {
    return tickets.every(ticket => {
      const ticketQuestions = this.getPerGuestQuestionConfigsForTicket(ticket)

      if (ticketQuestions.length === 0) {
        return true
      }

      return ticketQuestions.every(questionConfig => {
        return this.hasValidAnswerForPerGuestQuestion(ticket.refId, questionConfig, forceMandatory)
      })
    })
  }

  /**
   * checks if a per-booking question has a valid answer
   * @param questionConfig
   * @param forceMandatory
   */
  hasValidAnswerForQuestion(questionConfig: QuestionActivityConfiguration, forceMandatory: boolean = false): boolean {
    const parentIsMandatory = this.isFollowUpOfMandatoryQuestion(questionConfig.question.id)
    const isRequired = forceMandatory || questionConfig.isRequired || parentIsMandatory
    if (!isRequired) return true

    const existingAnswer = this.getQuestionAnswerById(questionConfig.question.id)
    if (!existingAnswer) return false

    switch (questionConfig.question.type) {
      case 'TEXT':
        return existingAnswer?.questionAnswerText?.trim() !== ''

      case 'CHECK_BOX':
        return Boolean(existingAnswer?.isChecked)

      case 'SELECT_ONE':
        return Boolean(existingAnswer?.questionAnswerOptionId)

      default:
        return false
    }
  }

  /**
   * checks if a per-guest question has a valid answer
   * @param ticketRefId
   * @param questionConfig
   * @param forceMandatory
   */
  hasValidAnswerForPerGuestQuestion(ticketRefId: string, questionConfig: QuestionActivityConfiguration, forceMandatory: boolean = false): boolean {
    const parentIsMandatory = this.isFollowUpOfMandatoryQuestion(questionConfig.question.id)
    const isRequired = forceMandatory || questionConfig.isRequired || parentIsMandatory
    if (!isRequired) return true

    const existingAnswer = this.perGuestQuestionAnswers.get(ticketRefId)?.find(answer => answer.questionId === questionConfig.question.id)
    if (!existingAnswer) return false

    switch (questionConfig.question.type) {
      case 'TEXT':
        return existingAnswer?.questionAnswerText?.trim() !== ''

      case 'CHECK_BOX':
        return Boolean(existingAnswer?.isChecked)

      case 'SELECT_ONE':
        return Boolean(existingAnswer?.questionAnswerOptionId)

      default:
        return false
    }
  }

  /**
   * recursively checks whether a follow-up question is mandatory as per any of its ancestors
   * @param questionId
   */
  isFollowUpOfMandatoryQuestion(questionId: string): boolean {
    const parentQuestionId = this.followUpQuestionParentMap.get(questionId)
    if (!parentQuestionId) return false

    const parentConfig = this.questionActivityConfigurations.find(config => config.question.id === parentQuestionId)
    if (!parentConfig) return false
    if (parentConfig.isRequired) return true

    return this.isFollowUpOfMandatoryQuestion(parentQuestionId)
  }

  /**
   * checks if all mandatory per-booking questions are answered
   * @param forceMandatory
   */
  areAllPerBookingRequiredQuestionsAnswered(forceMandatory: boolean = false): boolean {
    if (this.perBookingQuestionConfigs.length === 0) {
      return true
    }

    return this.perBookingQuestionConfigs.every(questionConfig => {
      return this.hasValidAnswerForQuestion(questionConfig, forceMandatory)
    })
  }
}

declare global {
  export type CustomQuestionStore = CustomQuestionStoreClass

  export interface StoreRegistry {
    'custom-question': CustomQuestionStore
  }
}
